// React
import { useMemo } from "react"

// Countries list
import {
	getCountryCode,
	getCountryDataList,
	getEmojiFlag,
	type ICountryData,
	type TCountryCode,
} from "countries-list"

// Translate
import { useTrans } from "@/lib/i18n"

// API types
import { CoreLocationCountryChoices } from "@/api/graphql/types"

// A Country entry from `getCountryDataList()` — already carries `iso2` and
// `iso3`. Kept as an alias so downstream code has a stable name to import,
// and re-typed with a `code` alias for iso2 for backward compatibility with
// the previous shape of this module.
export interface Country extends ICountryData {
	code: TCountryCode
}

// A list of countries with `code` aliased to `iso2` for downstream use.
export const countries: Country[] = getCountryDataList().map((country) => ({
	...country,
	code: country.iso2,
}))

/**
 * React hook to get core location country choices with reactive locale updates
 * Check which countries are allowed in the app according to this enum list CoreLocationCountryChoices
 * Returns a sorted list of allowed countries with translated names
 */
export function useOnlyCoreLocationCountryChoices() {
	const t = useTrans("locale")

	return useMemo(() => {
		const allowed = new Set<string>(
			Object.values(CoreLocationCountryChoices),
		)
		return countries
			.filter((country) => allowed.has(country.code))
			.map((country) => ({
				code: country.code as CoreLocationCountryChoices,
				name: t(`country.${country.code}`),
			}))
			.sort((a, b) => a.name.localeCompare(b.name))
	}, [t])
}

// Re-export
export { getCountryCode, getCountryDataList, getEmojiFlag }
export type { TCountryCode }
